/**
 * Rendu du terrain: des chunks de 50x50 m, grille de 1 m, ILE PAR ILE.
 *
 * Un seul materiau partage, couleurs par sommet, normales calculees a partir du
 * champ de hauteur (et non de la geometrie du chunk) pour que les bords de
 * chunks ne se voient pas. Les indices sont partages entre tous les chunks: un
 * seul tampon d'indices sur la carte graphique.
 *
 * POURQUOI PAR ILE, ET NON SUR TOUTE LA CARTE. Depuis le deuxieme monde, la
 * carte fait 1400 x 500 m: un quadrillage complet demanderait 280 chunks, donc
 * 280 appels de dessin, alors que le budget du projet est d'environ 150 POUR LA
 * SCENE ENTIERE. Or ce qu'il y a entre les deux iles n'est pas du terrain,
 * c'est de la MER — et le plateau continental de `client/render/eau.ts` existe
 * precisement pour etre « le sol marin hors du monde ». On ne quadrille donc
 * que la terre: 100 chunks pour l'ile 1, 16 pour l'ile 2, et rien entre les
 * deux.
 */
import * as THREE from 'three'
import { CHUNK_SIZE, TERRAIN_CELL } from '../../shared/constants.ts'
import type { Carte, Ile } from '../../shared/sim/carte.ts'
import { journal } from '../log/journal.ts'

/** Sommets par cote d'un chunk (51 pour 50 m au pas de 1 m). */
const SOMMETS = CHUNK_SIZE / TERRAIN_CELL + 1

/**
 * Normales lisses d'un bloc de terrain, deduites du gradient du champ de
 * hauteur: c'est ce qui efface la couture entre deux chunks voisins.
 */
function normalesLisses(terrain: Ile, ox: number, oz: number, sommets: number, pas: number): Float32Array {
  const out = new Float32Array(sommets * sommets * 3)
  const n: [number, number, number] = [0, 1, 0]
  let k = 0
  for (let j = 0; j < sommets; j++) {
    const z = oz + j * pas
    for (let i = 0; i < sommets; i++) {
      terrain.normale(ox + i * pas, z, n)
      out[k] = n[0]
      out[k + 1] = n[1]
      out[k + 2] = n[2]
      k += 3
    }
  }
  return out
}

/** Indices d'une grille reguliere, partages par tous les chunks. */
function indicesGrille(sommets: number): THREE.BufferAttribute {
  const quads = (sommets - 1) * (sommets - 1)
  const idx = new Uint16Array(quads * 6)
  let k = 0
  for (let j = 0; j < sommets - 1; j++) {
    for (let i = 0; i < sommets - 1; i++) {
      const a = j * sommets + i
      const b = a + 1
      const c = a + sommets
      const d = c + 1
      idx[k] = a
      idx[k + 1] = c
      idx[k + 2] = b
      idx[k + 3] = b
      idx[k + 4] = c
      idx[k + 5] = d
      k += 6
    }
  }
  return new THREE.BufferAttribute(idx, 1)
}

export class RenduTerrain {
  readonly groupe = new THREE.Group()

  private scene: THREE.Scene
  private materiau: THREE.MeshLambertMaterial
  private geometries: THREE.BufferGeometry[] = []
  private tris = 0

  constructor(scene: THREE.Scene, carte: Carte) {
    this.scene = scene
    this.groupe.name = 'terrain'
    this.materiau = new THREE.MeshLambertMaterial({ vertexColors: true })

    const t0 = performance.now()
    const indices = indicesGrille(SOMMETS)
    for (let n = 0; n < carte.iles.length; n++) {
      const ile = carte.iles[n]
      if (ile) this.construireIle(ile, n, indices)
    }

    scene.add(this.groupe)
    journal.info('terrain', `${this.geometries.length} chunks · ${(this.tris / 1000).toFixed(0)}k triangles`, {
      ms: Math.round(performance.now() - t0),
      iles: carte.iles.length,
    })
  }

  /** Le quadrillage d'une ile, en coordonnees du monde. */
  private construireIle(ile: Ile, numero: number, indices: THREE.BufferAttribute): void {
    const teinte = new THREE.Color()
    const chunks = Math.ceil(ile.cote / CHUNK_SIZE)

    for (let cz = 0; cz < chunks; cz++) {
      for (let cx = 0; cx < chunks; cx++) {
        const ox = ile.x0 + cx * CHUNK_SIZE
        const oz = ile.z0 + cz * CHUNK_SIZE
        const nb = SOMMETS * SOMMETS
        const positions = new Float32Array(nb * 3)
        const couleurs = new Float32Array(nb * 3)

        let k = 0
        for (let j = 0; j < SOMMETS; j++) {
          const lz = j * TERRAIN_CELL
          for (let i = 0; i < SOMMETS; i++) {
            const lx = i * TERRAIN_CELL
            const x = ox + lx
            const z = oz + lz
            positions[k] = lx
            positions[k + 1] = ile.hauteur(x, z)
            positions[k + 2] = lz
            teinte.setHex(ile.couleurSol(x, z))
            couleurs[k] = teinte.r
            couleurs[k + 1] = teinte.g
            couleurs[k + 2] = teinte.b
            k += 3
          }
        }

        const geo = new THREE.BufferGeometry()
        geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
        geo.setAttribute('normal', new THREE.BufferAttribute(normalesLisses(ile, ox, oz, SOMMETS, TERRAIN_CELL), 3))
        geo.setAttribute('color', new THREE.BufferAttribute(couleurs, 3))
        geo.setIndex(indices)

        const bornes = ile.bornesChunk(cx, cz)
        const demi = CHUNK_SIZE / 2
        const dy = (bornes.max - bornes.min) / 2
        geo.boundingSphere = new THREE.Sphere(
          new THREE.Vector3(demi, bornes.min + dy, demi),
          Math.sqrt(2 * demi * demi + dy * dy) + 0.01,
        )

        const maillage = new THREE.Mesh(geo, this.materiau)
        maillage.name = `chunk_${numero}_${cx}_${cz}`
        maillage.position.set(ox, 0, oz)
        maillage.receiveShadow = true
        maillage.castShadow = false
        maillage.matrixAutoUpdate = false
        maillage.updateMatrix()
        this.groupe.add(maillage)
        this.geometries.push(geo)
        this.tris += (SOMMETS - 1) * (SOMMETS - 1) * 2
      }
    }
  }

  nombreTriangles(): number {
    return this.tris
  }

  dispose(): void {
    this.scene.remove(this.groupe)
    for (const g of this.geometries) g.dispose()
    this.geometries.length = 0
    this.groupe.clear()
    this.materiau.dispose()
  }
}
